import { createContext, memo, useContext, useEffect, useRef } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

const MarkdownRendererContext = createContext<StoreApi<MarkdownRenderedContextState> | null>(null);

interface MarkdownRenderedContextState {
  highlights: string[];
  matchWholeWords: boolean;
}

interface MarkdownRendererContextProviderProps {
  highlights?: string[];
  matchWholeWords?: boolean;
  children: React.ReactNode;
}

const MarkdownRendererContextProviderBase = ({
  highlights = [],
  matchWholeWords = false,
  children,
}: MarkdownRendererContextProviderProps) => {
  const storeRef = useRef<StoreApi<MarkdownRenderedContextState> | null>(null);

  if (!storeRef.current) {
    storeRef.current = createStore<MarkdownRenderedContextState>((set) => ({
      matchWholeWords,
      highlights,
    }));
  }

  useEffect(() => {
    if (!storeRef.current) return;

    const currentState = storeRef.current.getState();

    if (currentState.matchWholeWords !== matchWholeWords || currentState.highlights !== highlights) {
      storeRef.current.setState({ highlights, matchWholeWords });
    }
  }, [highlights, matchWholeWords]);

  return <MarkdownRendererContext.Provider value={storeRef.current}>{children}</MarkdownRendererContext.Provider>;
};

export const MarkdownRendererContextProvider = memo(MarkdownRendererContextProviderBase);

export const useMarkdownRendererContext = <T,>(selector: (state: MarkdownRenderedContextState) => T) => {
  const store = useContext(MarkdownRendererContext);

  if (!store) {
    throw new Error('Missing MarkdownRendererContextProvider');
  }

  return useStore(store, selector);
};
